import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import type ReCAPTCHA from 'react-google-recaptcha';

import { SupportedLanguages } from '@/locales';
import { getErrorCode, stringifyError, QueryError } from '@/utils/errors';
import { Routes } from '@/core/router/routes.types';
import { computeCookieDomain, COOKIE_PATH, useAuthSuccess } from '@/hooks/useAuthSuccess';
import {
    useLazyMeQuery,
    useLazySamlProviderByEmailQuery,
    useLoginMutation,
} from '@/services/innovorder/auth/auth.endpoints';
import { isMfaRequiredData } from '@/services/innovorder/auth/auth.type';
import { getLanguage } from '@/redux/app';
import { ENV } from '@/utils/env';
import LoginPanel from './LoginPanel';
import logo from './logo.svg';
import { Header, Logo, ProductTitle } from './Login.style';

export { computeCookieDomain, COOKIE_PATH };

type LoginErrorBody = {
    code?: string;
    captchaRequired?: boolean;
    retryAfterSeconds?: number;
};

const readErrorBody = (error: QueryError): LoginErrorBody => {
    if (!error || !('data' in error) || typeof error.data !== 'object' || error.data === null) {
        return {};
    }
    const body = error.data as Record<string, unknown>;
    return {
        code: typeof body.code === 'string' ? body.code : undefined,
        captchaRequired: typeof body.captchaRequired === 'boolean' ? body.captchaRequired : undefined,
        retryAfterSeconds: typeof body.retryAfterSeconds === 'number' ? body.retryAfterSeconds : undefined,
    };
};

const LoginPage: React.FunctionComponent<React.PropsWithChildren> = () => {
    const [postLogin, { data, isSuccess, error }] = useLoginMutation();
    const [getMe, { data: meData, isSuccess: meSuccess, error: meError }] = useLazyMeQuery();
    const [checkSamlProvider] = useLazySamlProviderByEmailQuery();
    const { handleAuthSuccess, error: authSuccessError } = useAuthSuccess();
    const history = useHistory();
    const location = useLocation();
    const currentLocale = useSelector(getLanguage);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [samlUuid, setSamlUuid] = useState<string | null>(null);
    const [samlLoading, setSamlLoading] = useState(false);
    const [ssoError, setSsoError] = useState<string | null>(null);
    const [bearerToken, setBearerToken] = useState<string | null>(null);

    // -- Rate limiting / lockout state --------------------------------------------------
    const captchaRef = useRef<ReCAPTCHA>(null);
    const [captchaRequired, setCaptchaRequired] = useState(false);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [lockoutSecondsLeft, setLockoutSecondsLeft] = useState<number | null>(null);
    const [ipNotAllowed, setIpNotAllowed] = useState(false);
    const [accountDisabled, setAccountDisabled] = useState(false);
    const recaptchaSiteKey = ENV.RECAPTCHA_SITE_KEY;

    const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);

    const samlEnabled = Boolean(samlUuid);

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const bearerFromUrl = searchParams.get('bearer');
    const samlErrorFromUrl = searchParams.get('saml_error');
    const mfaTokenFromUrl = searchParams.get('mfaToken');
    const mfaStatusFromUrl = searchParams.get('mfaStatus');

    const clearLoginQueryParams = useCallback(() => {
        const params = new URLSearchParams(location.search);
        params.delete('bearer');
        params.delete('saml_error');
        params.delete('mfaToken');
        params.delete('mfaStatus');
        params.delete('providerId');
        params.delete('redirectUri');
        history.replace({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' });
    }, [history, location.pathname, location.search]);

    useEffect(() => {
        if (samlErrorFromUrl === 'login_failed') {
            setSsoError('login_failed');
            clearLoginQueryParams();
        }
        if (samlErrorFromUrl === 'account_disabled') {
            setAccountDisabled(true);
            clearLoginQueryParams();
        }
        if (bearerFromUrl) {
            setBearerToken(bearerFromUrl);
            // Clear URL immediately to avoid leaking token via copy/paste
            clearLoginQueryParams();
            getMe({ token: bearerFromUrl });
        }
        if (mfaTokenFromUrl && mfaStatusFromUrl) {
            clearLoginQueryParams();
            const nextRoute = mfaStatusFromUrl === 'mfa_setup_required' ? Routes.MfaSetup : Routes.MfaVerify;
            history.replace(nextRoute, { mfaToken: mfaTokenFromUrl });
        }
    }, [bearerFromUrl, clearLoginQueryParams, getMe, samlErrorFromUrl, mfaTokenFromUrl, mfaStatusFromUrl, history]);

    useEffect(() => {
        if (meSuccess && meData && bearerToken) {
            handleAuthSuccess(meData, bearerToken);
        }
    }, [meSuccess, meData, bearerToken]);

    useEffect(() => {
        if (!email || !isValidEmail(email)) {
            setSamlUuid(null);
            setSamlLoading(false);
            return;
        }
        // Always clear samlUuid immediately on email change so the password field
        // reappears while we re-check. We don't short-circuit on a "last checked
        // email" because the user could edit and revert to a previously-checked
        // email while samlUuid is cleared mid-flight, leaving the password field
        // visible for an SSO-configured domain (bypass).
        setSamlUuid(null);
        const timeout = window.setTimeout(async () => {
            setSamlLoading(true);
            try {
                const provider = await checkSamlProvider({ email }).unwrap();
                setSamlUuid(provider.uuid);
            } catch {
                setSamlUuid(null);
            } finally {
                setSamlLoading(false);
            }
        }, 300);
        return () => window.clearTimeout(timeout);
    }, [email, checkSamlProvider]);

    useEffect(() => {
        if (!isSuccess || !data) return;
        if (isMfaRequiredData(data)) {
            const nextRoute = data.methods && data.methods.length > 0 ? Routes.MfaVerify : Routes.MfaSetup;
            history.push(nextRoute, { mfaToken: data.mfaToken, methods: data.methods });
            return;
        }
        if (data.accessToken) {
            handleAuthSuccess(data.user, data.accessToken);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess, data]);

    useEffect(() => {
        if (!error) return;
        const code = getErrorCode(error);
        if (code === 'sso_login_required') {
            (async () => {
                try {
                    const provider = samlUuid ? { uuid: samlUuid } : await checkSamlProvider({ email }).unwrap();
                    if (provider?.uuid) {
                        window.location.assign(`${ENV.API_URL}/sso/saml/${provider.uuid}`);
                        return;
                    }
                } catch {
                    // fall through to the visible error state below
                }
                setSsoError('sso_login_required');
            })();
            return;
        }

        const body = readErrorBody(error);

        if (code === 'account_locked') {
            const seconds = body.retryAfterSeconds && body.retryAfterSeconds > 0 ? body.retryAfterSeconds : null;
            setLockoutSecondsLeft(seconds);
            captchaRef.current?.reset();
            setCaptchaToken(null);
            return;
        }

        if (code === 'ip_not_allowed') {
            setIpNotAllowed(true);
            return;
        }

        if (code === 'account_disabled') {
            setAccountDisabled(true);
            return;
        }

        if (code === 'captcha_required') {
            setCaptchaRequired(true);
            captchaRef.current?.reset();
            setCaptchaToken(null);
            return;
        }

        if (code === 'login_failed' && body.captchaRequired) {
            setCaptchaRequired(true);
            captchaRef.current?.reset();
            setCaptchaToken(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    useEffect(() => {
        if (authSuccessError) setSsoError(authSuccessError);
    }, [authSuccessError]);

    // Lockout countdown: tick every second and unlock UI when it hits zero.
    useEffect(() => {
        if (lockoutSecondsLeft === null) return;
        if (lockoutSecondsLeft <= 0) {
            setLockoutSecondsLeft(null);
            return;
        }
        const interval = window.setInterval(() => {
            setLockoutSecondsLeft((prev) => (prev === null || prev <= 1 ? null : prev - 1));
        }, 1000);
        return () => window.clearInterval(interval);
    }, [lockoutSecondsLeft]);

    const login = ({ email, password, rememberMe }: { email: string; password: string; rememberMe: boolean }) => {
        if (samlEnabled && samlUuid && email) {
            window.location.assign(`${ENV.API_URL}/sso/saml/${samlUuid}`);
            return;
        }

        if (lockoutSecondsLeft !== null && lockoutSecondsLeft > 0) return;
        if (captchaRequired && !captchaToken) return;
        if (ipNotAllowed) return;
        if (accountDisabled) return;

        if (email && password) {
            postLogin({
                locale: currentLocale || SupportedLanguages.fr,
                email,
                password,
                rememberMe,
                ...(captchaToken ? { captchaToken } : {}),
            });
        }
    };

    const errorMessage = accountDisabled ? (
        <FormattedMessage id="login.accountDisabled" />
    ) : ipNotAllowed ? (
        <FormattedMessage id="login.ipNotAllowed" />
    ) : lockoutSecondsLeft !== null && lockoutSecondsLeft > 0 ? (
        <FormattedMessage
            id="login.accountLocked"
            values={{ minutes: Math.max(1, Math.ceil(lockoutSecondsLeft / 60)) }}
        />
    ) : ssoError === 'login_failed' ? (
        <FormattedMessage id="login.ssoFailed" />
    ) : ssoError === 'sso_login_required' ? (
        <FormattedMessage id="login.ssoLoginRequired" />
    ) : ssoError === 'no_brands_configured' ? (
        <FormattedMessage id="login.noBrandsConfigured" />
    ) : ssoError === 'guest_brand_login_failed' ? (
        <FormattedMessage id="login.guestBrandLoginFailed" />
    ) : captchaRequired && getErrorCode(error) === 'captcha_required' ? (
        <FormattedMessage id="login.captchaRequired" />
    ) : (
        stringifyError(meError) || stringifyError(error)
    );

    return (
        <div>
            <Header>
                <Logo src={logo} />
                <ProductTitle>Backoffice</ProductTitle>
            </Header>
            <LoginPanel
                email={email}
                password={password}
                onEmailChange={(val) => {
                    setEmail(val);
                    setSsoError(null);
                    setIpNotAllowed(false);
                    setAccountDisabled(false);
                }}
                onPasswordChange={setPassword}
                samlEnabled={samlEnabled}
                samlLoading={samlLoading}
                onSubmit={login}
                errorMessage={errorMessage}
                captchaRequired={captchaRequired}
                captchaSiteKey={recaptchaSiteKey}
                captchaRef={captchaRef}
                onCaptchaChange={setCaptchaToken}
                captchaToken={captchaToken}
                lockoutSecondsLeft={lockoutSecondsLeft}
                ipNotAllowed={ipNotAllowed}
            />
        </div>
    );
};

export default LoginPage;
